import { BusinessNote, BusinessNoteLocation } from "common/types";

export interface GroupedData {
  notes: BusinessNote[];
  taskDetails?: {
    id: number;
    task: string;
  };
  floor?: BusinessNoteLocation;
}

export function groupNotesByFloor(
  businessNotes: BusinessNote[],
): GroupedData[] {
  // First, collect all unique floors
  const allFloors: Map<string, BusinessNoteLocation> = new Map();

  businessNotes.forEach((note) => {
    note.locationAreasData.forEach((location) => {
      if (location.locationType === "Floor") {
        // Store the floor with its name as the key
        allFloors.set(location.locationName, location);
      } else if (location.locationType === "Category") {
        // If the location is a category, we can also add it to the map
        allFloors.set(location.categoryHierarchy, location);
      } else if (location.locationType === "Space") {
        // If the location is a space, we can also add it to the map
        allFloors.set(location.locationName, location);
      } else if (location.locationType === "Business") {
        // If the location is a business, we can also add it to the map
        allFloors.set(location.locationType, location);
      }
    });
  });

  // Create an array to hold our grouped data
  const result: GroupedData[] = [];

  // Initialize the array with one entry per floor
  allFloors.forEach((floorLocation) => {
    result.push({
      notes: [],
      floor: floorLocation,
      taskDetails: undefined,
    });
  });

  // Add notes to their respective floors
  businessNotes.forEach((note) => {
    note.locationAreasData.forEach((location) => {
      if (location.locationType === "Floor") {
        const floorName = location.locationName;

        // Find the corresponding group in our result array
        const floorGroup = result.find(
          (group) => group.floor?.locationName === floorName,
        );

        if (floorGroup) {
          // Check if this note is already added to this floor
          const noteAlreadyAdded = floorGroup.notes.some(
            (existingNote) =>
              existingNote.business_notes_id === note.business_notes_id,
          );

          // Add the note to the floor if it's not already there
          if (!noteAlreadyAdded) {
            floorGroup.notes.push(note);
          }
        }
      } else if (location.locationType === "Category") {
        // If the location is a category, we can also add it to the map
        const categoryName = location.categoryHierarchy;

        // Find the corresponding group in our result array
        const categoryGroup = result.find(
          (group) => group.floor?.categoryHierarchy === categoryName,
        );

        if (categoryGroup) {
          // Check if this note is already added to this category
          const noteAlreadyAdded = categoryGroup.notes.some(
            (existingNote) =>
              existingNote.business_notes_id === note.business_notes_id,
          );

          // Add the note to the category if it's not already there
          if (!noteAlreadyAdded) {
            categoryGroup.notes.push(note);
          }
        }
      } else if (location.locationType === "Space") {
        // If the location is a space, we can also add it to the map
        const spaceName = location.locationName;

        // Find the corresponding group in our result array
        const spaceGroup = result.find(
          (group) => group.floor?.locationName === spaceName,
        );

        if (spaceGroup) {
          // Check if this note is already added to this space
          const noteAlreadyAdded = spaceGroup.notes.some(
            (existingNote) =>
              existingNote.business_notes_id === note.business_notes_id,
          );

          // Add the note to the space if it's not already there
          if (!noteAlreadyAdded) {
            spaceGroup.notes.push(note);
          }
        }
      } else if (location.locationType === "Business") {
        // Find the corresponding group in our result array
        const businessGroup = result.find(
          (group) => group.floor?.locationType === location.locationType,
        );

        if (businessGroup) {
          // Check if this note is already added to this business
          const noteAlreadyAdded = businessGroup.notes.some(
            (existingNote) =>
              existingNote.business_notes_id === note.business_notes_id,
          );

          // Add the note to the business if it's not already there
          if (!noteAlreadyAdded) {
            businessGroup.notes.push(note);
          }
        }
      }
    });
  });

  return result;
}

export function sortGroupedData(groupedData: GroupedData[]): GroupedData[] {
  const floorOrder: { [key: string]: number } = {
    "Ground Floor": -1,
    Basement: -2,
    "Lower Ground": -1.5,
  };

  return [...groupedData].sort((a, b) => {
    const floorNameA = a.floor?.locationName || "";
    const floorNameB = b.floor?.locationName || "";

    // Handle special floor names first
    if (
      floorOrder[floorNameA] !== undefined &&
      floorOrder[floorNameB] !== undefined
    ) {
      return floorOrder[floorNameA] - floorOrder[floorNameB];
    }

    if (floorOrder[floorNameA] !== undefined) return -1;
    if (floorOrder[floorNameB] !== undefined) return 1;

    // Handle numbered floors (First Floor, Second Floor, etc.)
    const floorMap: { [key: string]: number } = {
      First: 1,
      Second: 2,
      Third: 3,
      Fourth: 4,
      Fifth: 5,
      Sixth: 6,
      Seventh: 7,
      Eighth: 8,
      Ninth: 9,
      Tenth: 10,
    };

    // Extract floor names like"First","Second", etc.
    const textFloorA = floorNameA.replace(" Floor", "");
    const textFloorB = floorNameB.replace(" Floor", "");

    if (
      floorMap[textFloorA] !== undefined &&
      floorMap[textFloorB] !== undefined
    ) {
      return floorMap[textFloorA] - floorMap[textFloorB];
    }

    // If one is a named floor and one is a numbered floor
    if (floorMap[textFloorA] !== undefined) return -1;
    if (floorMap[textFloorB] !== undefined) return 1;

    // For numbered floors (1st Floor, 2nd Floor, or just numbers)
    const floorNumberA = floorNameA.match(/(\d+)/)?.[0] || "";
    const floorNumberB = floorNameB.match(/(\d+)/)?.[0] || "";

    if (floorNumberA && floorNumberB) {
      return parseInt(floorNumberA) - parseInt(floorNumberB);
    }

    // Fallback to alphabetical sorting
    return floorNameA.localeCompare(floorNameB);
  });
}
